import api from './api';

export interface DepartureTimeInfo {
  departureTime: string | null; // null for "no travel required" scenarios
  isApproximate: boolean;
  travelTimeMinutes: number;
  distanceMeters?: number;
  noTravelRequired?: boolean; // indicates same location as previous match
  baseTravelTimeMinutes?: number;  // raw Google Maps time before buffers
  bufferMinutes?: number;          // total buffer added (traffic + human)
  warmupMinutes?: number;          // warmup time (60)
  origin: {
    type: 'home' | 'previous_match' | 'other_player_match';
    location: string;
    playerId?: string;
    latitude?: number;
    longitude?: number;
  };
}

export type RecentChangeBadge = 'scheduled' | 'rescheduled' | 'moved' | 'new';

export interface RecentChangeInfo {
  badge: RecentChangeBadge;
  changedAt: string;
}

export interface VenueOverride {
  id: string;
  organizationId: string;
  venueName: string;
  address?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  latitude?: number;
  longitude?: number;
}

export interface HourlyForecastPeriod {
  time: string;
  temperature: number;
  shortForecast: string;
  precipChance: number | null;
  windSpeed: string | null;
  isDaytime: boolean;
  humidity: number | null;
}

export interface MatchWeatherData {
  temperature: number | null;
  temperatureUnit: string;
  shortForecast: string | null;
  precipChance: number | null;
  windSpeed: string | null;
  isDaytime: boolean;
  humidity: number | null;
  feelsLike: number | null;
  aqi: number | null;
  aqiCategory: string | null;
  uvIndex: number | null;
  forecastAvailable: boolean;
  hourly?: HourlyForecastPeriod[];
}

export interface ScheduleMatch {
  id: string;
  homeTeam: string;
  homeTeamId?: string;
  awayTeam: string;
  awayTeamId?: string;
  homeTeamLogo?: {
    logoPath?: string;
    logoUrl?: string;
  };
  awayTeamLogo?: {
    logoPath?: string;
    logoUrl?: string;
  };
  dateTime: string | null; // Made nullable for TBD matches
  location?: string;
  address?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  latitude?: number;
  longitude?: number;
  locationConfirmed: boolean;
  clubLocationFallback: boolean;
  status: 'SCHEDULED' | 'TBD' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'POSTPONED'; // Added TBD
  matchType: 'REGULAR_SEASON' | 'TOURNAMENT' | 'PLAYOFF' | 'FRIENDLY';
  homeScore?: number;
  awayScore?: number;
  scoreVerified?: boolean;
  timezoneVerified?: boolean;
  league: {
    id: string;
    name: string;
    division?: string;
    logoPath?: string;
    logoUrl?: string;
  };
  homeClub?: {
    id: string;
    name: string;
    logoUrl?: string;
    logoSmallUrl?: string;
    logoMediumUrl?: string;
  };
  awayClub?: {
    id: string;
    name: string;
    logoUrl?: string;
    logoSmallUrl?: string;
    logoMediumUrl?: string;
  };
  tournament?: {
    id: string;
    name: string;
  };
  // Add address fields from importSource
  importSource?: {
    address?: string;
    city?: string;
    state?: string;
    [key: string]: any;
  };
  players: {
    id: string;
    name: string;
    profilePhotoType?: string;
    profilePhotoUrl?: string | null;
    email?: string | null;
    playerColor?: string;
    teamName: string;
    isHome: boolean;
    isCurrentUser: boolean;
    ownerLabel: string;
    hasAddress?: boolean;
    homeLatitude?: number;
    homeLongitude?: number;
  }[];
  departureTimes?: {
    [playerId: string]: DepartureTimeInfo;
  };
  // Whether this match's venue data comes from a manual override
  venueOverride?: boolean;
  // Recent schedule change badge (shown for 7 days after change)
  recentChange?: RecentChangeInfo | null;
}

export interface ScheduleFilters {
  startDate?: string;
  endDate?: string;
  playerIds?: string[]; // Changed from playerId to playerIds array
  leagueId?: string;
  status?: string;
}

export interface TeamMatchesQuery {
  page?: number;
  limit?: number;
  division?: string;
  globalTeamId?: string;
  teamId?: string;
}

export interface TeamMatchesResponse {
  matches: {
    completed: ScheduleMatch[];
    upcoming: ScheduleMatch[];
    pendingTBD: ScheduleMatch[];
  };
  pagination: {
    currentPage: number;
    totalPages: number;
    totalMatches: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  teamInfo: {
    teamName: string;
    division?: string;
  };
}

export interface CreateMatchRequest {
  leagueId: string;
  homeTeam: string;
  awayTeam: string;
  dateTime: string;
  location?: string;
  matchType?: 'REGULAR_SEASON' | 'TOURNAMENT' | 'PLAYOFF' | 'FRIENDLY';
  tournamentId?: string;
}

export interface UpdateMatchRequest {
  homeTeam?: string;
  awayTeam?: string;
  dateTime?: string;
  location?: string;
  // status field removed - users can no longer update match status
  homeScore?: number;
  awayScore?: number;
}

export const scheduleService = {
  async getSchedule(filters?: ScheduleFilters): Promise<ScheduleMatch[]> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.playerIds && filters.playerIds.length > 0) {
      filters.playerIds.forEach(playerId => params.append('playerId', playerId));
    }
    if (filters?.leagueId && filters.leagueId !== 'all') params.append('leagueId', filters.leagueId);
    if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
    // Send the user's timezone so the backend can convert date boundaries to UTC
    params.append('tz', Intl.DateTimeFormat().resolvedOptions().timeZone);

    const response = await api.get(`/schedule?${params.toString()}`);
    return response.data.matches;
  },

  async getUpcomingMatches(days: number = 7): Promise<ScheduleMatch[]> {
    const response = await api.get(`/schedule/upcoming?days=${days}`);
    return response.data.matches;
  },

  async getUpcomingMatchCount(days: number = 7): Promise<number> {
    const response = await api.get(`/schedule/upcoming/count?days=${days}`);
    return response.data.count;
  },

  async getUpcomingMatchSummary(days: number = 365): Promise<{
    count: number;
    nextMatch: {
      homeTeam: string;
      awayTeam: string;
      dateTime: string | null;
      location: string | null;
      status: string;
      league: { name: string };
    } | null;
  }> {
    const response = await api.get(`/schedule/upcoming/summary?days=${days}`);
    return response.data;
  },

  async getMatchDetails(matchId: string): Promise<ScheduleMatch> {
    const response = await api.get(`/schedule/matches/${matchId}`);
    return response.data.match;
  },

  async createMatch(matchData: CreateMatchRequest): Promise<ScheduleMatch> {
    const response = await api.post('/schedule/matches', matchData);
    return response.data.match;
  },

  async updateMatch(matchId: string, updates: UpdateMatchRequest): Promise<ScheduleMatch> {
    const response = await api.put(`/schedule/matches/${matchId}`, updates);
    return response.data.match;
  },

  async getDepartureTimes(matchIds: string[]): Promise<{ departureTimes: Record<string, Record<string, DepartureTimeInfo>>; pending: number }> {
    const params = new URLSearchParams();
    params.append('matchIds', matchIds.join(','));
    const response = await api.get(`/schedule/departure-times?${params.toString()}`);
    return { departureTimes: response.data.departureTimes, pending: response.data.pending || 0 };
  },

  async setVenueOverride(venueName: string, address: string): Promise<VenueOverride> {
    const response = await api.put('/schedule/venue-override', { venueName, address });
    return response.data.override;
  },

  async getWeather(matchIds: string[]): Promise<{ weather: Record<string, MatchWeatherData>; pending: number; pendingMatchIds?: string[] }> {
    const params = new URLSearchParams();
    params.append('matchIds', matchIds.join(','));
    const response = await api.get(`/schedule/weather?${params.toString()}`);
    return { weather: response.data.weather, pending: response.data.pending || 0, pendingMatchIds: response.data.pendingMatchIds };
  },

  async removeVenueOverride(id: string): Promise<void> {
    await api.delete(`/schedule/venue-override/${id}`);
  },

  async getTeamMatches(
    leagueId: string, 
    teamName: string, 
    query?: TeamMatchesQuery
  ): Promise<TeamMatchesResponse> {
    const params = new URLSearchParams();
    if (query?.page) params.append('page', query.page.toString());
    if (query?.limit) params.append('limit', query.limit.toString());
    if (query?.division) params.append('division', query.division);
    if (query?.globalTeamId) params.append('globalTeamId', query.globalTeamId);
    if (query?.teamId) params.append('teamId', query.teamId);

    const response = await api.get(
      `/schedule/leagues/${encodeURIComponent(leagueId)}/teams/${encodeURIComponent(teamName)}/matches?${params.toString()}`
    );
    return response.data;
  },
};
